// The team's admin page (/admin): statistics, who uses the app and who stopped, plan changes and
// follow-up. Only emails in the backend's CRM_ADMIN_EMAILS get data; everyone else sees a notice.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiDownload, FiMail } from 'react-icons/fi';
import authHeaders from '../authHeaders';
import { Bars, Shares } from './AdminCharts';
import AdminBulkEmail from './AdminBulkEmail';
import AdminCampaigns from './AdminCampaigns';
import AdminUserPanel from './AdminUserPanel';
import { API, PLANS, SEGMENTS, STAGES, ago, api, day, errorText, identity, isStaging, number, percent } from './adminFormat';
import './admin.css';

const DEFAULTS = {
  tab: 'overview', days: '30', search: '', status: '', crm_status: '', plan: '', channel: '',
  inactive_days: '30', sort: 'last_active', page: '1', user: '',
};
const FILTERS = ['search', 'status', 'crm_status', 'plan', 'channel', 'inactive_days', 'sort', 'page'];
const AUDIENCE = ['search', 'status', 'crm_status', 'plan', 'channel', 'inactive_days'];   // who, not how it is shown
const SORTS = [['last_active', 'Last active'], ['inactive', 'Longest without use'], ['created', 'Newest sign-ups'],
  ['questions', 'Most questions'], ['follow_up', 'Follow-up date'], ['email', 'Email']];
const PAGE_SIZE = 50;

// Tab, filters and the open user live in the URL: links can be shared and Back works.
function useAdminParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(Object.keys(DEFAULTS).map((key) => [key, searchParams.get(key) ?? DEFAULTS[key]]));
  const update = useCallback((changes, replace = false) => setSearchParams((previous) => {
    const next = new URLSearchParams(previous);
    Object.entries(changes).forEach(([key, value]) => {
      if (value == null || String(value) === DEFAULTS[key]) next.delete(key); else next.set(key, String(value));
    });
    return next;
  }, { replace }), [setSearchParams]);
  return [params, update];
}

function Problem({ message, onRetry }) {
  return <div className="adm-problem" role="alert"><p>{message}</p>{onRetry && <button type="button" className="ws-secondary" onClick={onRetry}>Try again</button>}</div>;
}

function Overview({ days, update }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setError('');
    api.get('/stats', { days })
      .then((data) => { if (active) setStats(data); })
      .catch((err) => { if (active) setError(errorText(err)); });
    return () => { active = false; };
  }, [days, retry]);
  if (error) return <Problem message={error} onRetry={() => setRetry((n) => n + 1)} />;
  if (!stats) return <p className="adm-muted" role="status">Loading…</p>;

  const t = stats.totals;
  const showUsers = (filters) => update({ ...Object.fromEntries(FILTERS.map((key) => [key, null])), tab: 'users', ...filters });
  const cards = [
    { label: 'Users', value: t.users, note: `+${number(t.new_users)} in ${stats.days} days` },
    { label: 'Asked today', value: t.dau },
    { label: 'Asked in 7 days', value: t.wau, note: `${percent(t.wau, t.users)} of users`, filters: { status: 'active' } },
    { label: 'Asked in 30 days', value: t.mau, note: `${percent(t.mau, t.users)} of users` },
    { label: 'Stopped asking', value: t.inactive_30, note: 'nothing for 30+ days', filters: { status: 'inactive' } },
    { label: 'Never asked', value: t.never_used, note: `${number(t.opened_never_asked)} opened the app`, filters: { status: 'never', sort: 'created' } },
    { label: 'Questions asked', value: t.questions, note: `${percent(t.questions_whatsapp, t.questions)} on WhatsApp` },
    { label: 'WhatsApp users', value: t.whatsapp, note: `${number(t.whatsapp_only)} with no email`, filters: { channel: 'whatsapp' } },
    { label: 'Follow-ups due', value: t.follow_up_due, note: 'date reached', filters: { status: 'follow_up', sort: 'follow_up' } },
    { label: 'Plans set by the team', value: t.manual_plans },
    { label: 'Unsubscribed from news', value: t.unsubscribed ?? 0, note: 'official emails still reach them', filters: { status: 'unsubscribed' } },
    { label: 'Suspended', value: t.suspended, filters: { status: 'suspended' } },
  ];
  const orphans = stats.whatsapp_numbers_without_account;
  if (orphans?.numbers) cards.push({ label: 'WhatsApp numbers with no account', value: orphans.numbers, note: `${number(orphans.active_30)} asked in 30 days` });
  const average = stats.activity.reduce((sum, row) => sum + row.users, 0) / Math.max(stats.activity.length, 1);
  const peak = Math.max(0, ...stats.activity.map((row) => row.users));
  const signups = stats.signups.reduce((sum, row) => sum + row.n, 0);
  const label = (key) => ({ whatsapp: 'WhatsApp', web: 'Web' }[key] || key);

  return <>
    <div className="adm-toolbar">
      <label className="ws-field">Period
        <select value={days} onChange={(event) => update({ days: event.target.value })}>
          {[['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days'], ['365', 'Last 12 months']].map(([value, text]) => <option key={value} value={value}>{text}</option>)}
        </select>
      </label>
    </div>
    <div className="adm-kpis">
      {cards.map((card) => {
        const body = <><span>{card.label}</span><strong>{number(card.value)}</strong>{card.note && <small>{card.note}</small>}</>;
        return card.filters
          ? <button key={card.label} type="button" className="adm-kpi" onClick={() => showUsers(card.filters)} title="See who">{body}</button>
          : <div key={card.label} className="adm-kpi">{body}</div>;
      })}
    </div>
    <div className="adm-charts">
      <Bars label="People who asked something, per day" rows={stats.activity} value={(row) => row.users}
        summary={`${average.toFixed(1)} a day · peak ${peak}`}
        describe={(row) => `${row.day}: ${row.users} users, ${row.conversations} conversations`} />
      <Bars label="Sign-ups per day" rows={stats.signups} value={(row) => row.n}
        summary={`${number(signups)} in ${stats.days} days`} describe={(row) => `${row.day}: ${row.n} sign-ups`} />
      <Shares label="Plans" total={t.users}
        rows={Object.entries(stats.plans).map(([plan, n]) => ({ key: plan || 'none', label: plan || 'none', value: n }))} />
      <Shares label={`Channels used in ${stats.days} days`}
        rows={Object.entries(stats.channels).map(([key, c]) => ({ key, label: label(key), value: c.users, note: `${number(c.questions)} questions · ${number(c.conversations)} conversations` }))} />
      <Shares label="Languages" total={t.users}
        rows={Object.entries(stats.languages).map(([key, n]) => ({ key, label: key, value: n }))} />
    </div>
  </>;
}

function Users({ params, update, refresh, sending }) {
  const [data, setData] = useState(null);
  // Selection for a group email: people ticked one by one (any page), or everyone matching the filters.
  const [selected, setSelected] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [bulk, setBulk] = useState(null);
  const pageBox = useRef(null);
  const audienceKey = JSON.stringify(Object.fromEntries(AUDIENCE.map((key) => [key, params[key]])));
  useEffect(() => { setSelected(new Set()); setAllMatching(false); }, [audienceKey]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState(params.search);
  const query = JSON.stringify(Object.fromEntries(FILTERS.map((key) => [key, params[key]])));

  useEffect(() => { setSearch(params.search); }, [params.search]);
  useEffect(() => {   // typing waits a moment before it becomes a filter
    if (search.trim() === params.search) return undefined;
    const timer = setTimeout(() => update({ search: search.trim(), page: null }, true), 300);
    return () => clearTimeout(timer);
  }, [search, params.search, update]);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    api.get('/users', { ...JSON.parse(query), page_size: PAGE_SIZE })
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, refresh]);

  const filter = (key) => (event) => update({ [key]: event.target.value, page: null });
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data: blob } = await axios.get(`${API}/users.csv`, { headers: authHeaders(), params: JSON.parse(query), responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `upya-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(errorText(err)); }
    finally { setExporting(false); }
  };
  const page = Number(params.page) || 1;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const pageIds = data ? data.users.map((user) => user.id) : [];
  const pageAll = pageIds.length > 0 && pageIds.every((id) => allMatching || selected.has(id));
  const pageSome = pageIds.some((id) => allMatching || selected.has(id));
  useEffect(() => { if (pageBox.current) pageBox.current.indeterminate = pageSome && !pageAll; });
  const toggle = (id) => {
    if (allMatching) { setAllMatching(false); setSelected(new Set(pageIds.filter((x) => x !== id))); return; }
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const togglePage = () => {
    setAllMatching(false);
    setSelected((previous) => {
      const next = new Set(previous);
      pageIds.forEach((id) => { if (pageAll) next.delete(id); else next.add(id); });
      return next;
    });
  };
  const clearSelection = () => { setSelected(new Set()); setAllMatching(false); };
  const count = allMatching ? data?.total || 0 : selected.size;

  return <>
    <div className="adm-segments" role="group" aria-label="Who">
      {SEGMENTS.map(([value, text]) => <button key={value || 'all'} type="button" aria-pressed={params.status === value} onClick={() => update({ status: value, page: null })}>{text}</button>)}
    </div>
    <div className="adm-toolbar">
      <label className="ws-field adm-search">Search<input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Email, name, phone, organization" /></label>
      {params.status === 'inactive' && <label className="ws-field">Without use for
        <select value={params.inactive_days} onChange={filter('inactive_days')}>
          {['14', '30', '60', '90', '180'].map((n) => <option key={n} value={n}>{`${n}+ days`}</option>)}
        </select>
      </label>}
      <label className="ws-field">Plan
        <select value={params.plan} onChange={filter('plan')}><option value="">All plans</option>{PLANS.map((p) => <option key={p} value={p}>{p}</option>)}</select>
      </label>
      <label className="ws-field">Stage
        <select value={params.crm_status} onChange={filter('crm_status')}><option value="">All stages</option>{Object.entries(STAGES).map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select>
      </label>
      <label className="ws-field">Channel
        <select value={params.channel} onChange={filter('channel')}><option value="">All channels</option><option value="web">Web</option><option value="whatsapp">WhatsApp</option></select>
      </label>
      <label className="ws-field">Sort
        <select value={params.sort} onChange={filter('sort')}>{SORTS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select>
      </label>
      <button type="button" className="ws-secondary" onClick={exportCsv} disabled={exporting || !data?.total}><FiDownload aria-hidden="true" />{exporting ? 'Exporting…' : 'Export CSV'}</button>
    </div>
    {error && <Problem message={error} />}
    {data && count > 0 && <div className="adm-selection" role="region" aria-label="Selection">
      <span>{allMatching ? `All ${number(data.total)} people matching these filters are selected.` : `${number(selected.size)} selected.`}</span>
      {!allMatching && pageAll && data.total > pageIds.length && <button type="button" className="adm-link" onClick={() => setAllMatching(true)}>Select all {number(data.total)} matching these filters</button>}
      <button type="button" className="adm-link" onClick={clearSelection}>Clear selection</button>
      <button type="button" className="ws-primary" onClick={() => setBulk(allMatching ? { filters: JSON.parse(audienceKey) } : { user_ids: [...selected] })}><FiMail aria-hidden="true" />Email them</button>
    </div>}
    {data && <>
      <div className="adm-table-wrap" aria-busy={loading}>
        <table className="adm-table">
          <thead><tr><th className="adm-check-cell"><input ref={pageBox} type="checkbox" aria-label="Select everyone on this page" checked={pageAll} onChange={togglePage} disabled={!pageIds.length} /></th><th>User</th><th>Plan</th><th>Last active</th><th className="adm-num">Conversations</th><th className="adm-num">Questions</th><th>Channels</th><th>Stage</th><th>Follow-up</th></tr></thead>
          <tbody>
            {data.users.length === 0 && <tr><td colSpan={9} className="adm-muted">Nobody matches these filters.</td></tr>}
            {data.users.map((user) => <tr key={user.id} onClick={() => update({ user: user.id })} aria-selected={allMatching || selected.has(user.id)}>
              <td className="adm-check-cell" onClick={(event) => event.stopPropagation()}>
                <input type="checkbox" aria-label={`Select ${identity(user).title}`} checked={allMatching || selected.has(user.id)} onChange={() => toggle(user.id)} />
              </td>
              <td><button type="button" className="adm-user-link" onClick={(event) => { event.stopPropagation(); update({ user: user.id }); }}>
                <strong>{identity(user).title}</strong>{identity(user).subtitle && <small>{identity(user).subtitle}</small>}
                {user.unsubscribed_at && <small className="adm-muted">No news emails</small>}
              </button>{user.crm.organization && <small className="adm-muted"> {user.crm.organization}</small>}</td>
              <td><span className="adm-badge">{user.plan}</span>{user.suspended && <span className="adm-badge adm-badge-warn">suspended</span>}</td>
              <td title={user.last_active ? day(user.last_active) : undefined}>{ago(user.last_active) || <span className="adm-muted">never</span>}</td>
              <td className="adm-num">{number(user.conversations)}</td>
              <td className="adm-num" title={`${user.questions_web} on the web, ${user.questions_whatsapp} on WhatsApp`}>{number(user.questions)}</td>
              <td>{user.channels.map((c) => (c === 'whatsapp' ? 'WhatsApp' : 'Web')).join(' + ') || <span className="adm-muted">–</span>}</td>
              <td>{STAGES[user.crm.status] || user.crm.status}</td>
              <td>{user.crm.follow_up_at ? day(user.crm.follow_up_at) : <span className="adm-muted">–</span>}</td>
            </tr>)}
          </tbody>
        </table>
      </div>
      <div className="adm-pager">
        <span>{data.total ? `${number((page - 1) * PAGE_SIZE + 1)}–${number(Math.min(page * PAGE_SIZE, data.total))} of ${number(data.total)}` : '0 users'}</span>
        <button type="button" className="ws-secondary" disabled={page <= 1 || loading} onClick={() => update({ page: page - 1 })}>Previous</button>
        <button type="button" className="ws-secondary" disabled={page >= pages || loading} onClick={() => update({ page: page + 1 })}>Next</button>
      </div>
    </>}
    {!data && loading && <p className="adm-muted" role="status">Loading…</p>}
    {bulk && <AdminBulkEmail audience={bulk} sending={sending} onClose={() => setBulk(null)} />}
  </>;
}

export default function AdminPage() {
  const [params, update] = useAdminParams();
  const [access, setAccess] = useState({ state: 'checking' });
  const [retry, setRetry] = useState(0);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setAccess({ state: 'checking' });
    api.get('/me')
      .then((me) => { if (active) setAccess({ state: 'ok', email: me.email, sending: me.sending }); })
      .catch((err) => {
        const status = err?.response?.status;
        if (active) setAccess({ state: status === 401 || status === 403 ? 'denied' : 'error' });
      });
    return () => { active = false; };
  }, [retry]);
  const closeUser = useCallback(() => update({ user: null }), [update]);
  const changed = useCallback(() => setRefresh((n) => n + 1), []);

  const back = <Link className="ws-account-back" to="/chat"><FiArrowLeft aria-hidden="true" />Back to chat</Link>;
  if (access.state !== 'ok') {
    return <div className="adm-page">{back}<div className="adm-gate">
      {access.state === 'checking' && <p role="status">Checking access…</p>}
      {access.state === 'denied' && <><h1>Team only</h1><p>This page is for the UpyaChat team.</p></>}
      {access.state === 'error' && <Problem message="The admin service did not answer." onRetry={() => setRetry((n) => n + 1)} />}
    </div></div>;
  }
  const tabs = [['overview', 'Overview'], ['users', 'Users'], ['emails', 'Group emails']];
  return <div className="adm-page">
    {back}
    <header className="adm-heading">
      <h1>Users &amp; activity</h1>
      <p>Who uses UpyaChat, who stopped, and the team&apos;s follow-up. &quot;Active&quot; means asking something, on the web or on WhatsApp. Signed in as {access.email}.</p>
    </header>
    {isStaging && <p className="adm-notice" role="note">Staging: these figures come from a copy of production taken in July 2026, plus test accounts. Real activity since then is only in production.</p>}
    <div className="adm-tabs" role="tablist" aria-label="Admin sections">
      {tabs.map(([value, text]) => <button key={value} type="button" role="tab" aria-selected={params.tab === value} onClick={() => update({ tab: value })}>{text}</button>)}
    </div>
    {params.tab === 'users' ? <Users params={params} update={update} refresh={refresh} sending={access.sending} />
      : params.tab === 'emails' ? <AdminCampaigns /> : <Overview days={params.days} update={update} />}
    {params.user && <AdminUserPanel userId={params.user} sending={access.sending} onClose={closeUser} onChanged={changed} />}
  </div>;
}
